import englishPublicSiteContent from '../../content/publicSiteContent.json'
import { PUBLIC_SITE_CHROME } from './publicSiteChrome'
import {
  PUBLIC_SITE_LOCALE_LABELS,
  PUBLIC_SITE_TYPOGRAPHY,
  normalizePublicSiteLocale,
} from './localePolicy'
import type {
  DeepPartial,
  PublicSiteChrome,
  PublicSiteContent,
  PublicSiteLocale,
  PublicSiteLocaleOverride,
  PublicSiteNls,
} from './publicSiteTypes'
import { arPublicSiteOverride } from './locales/ar'
import { esPublicSiteOverride } from './locales/es'
import { frPublicSiteOverride } from './locales/fr'
import { hiPublicSiteOverride } from './locales/hi'
import { ptPublicSiteOverride } from './locales/pt'
import { zhHansPublicSiteOverride } from './locales/zh-Hans'

const OVERRIDES: Partial<Record<PublicSiteLocale, PublicSiteLocaleOverride>> = {
  ar: arPublicSiteOverride,
  es: esPublicSiteOverride,
  fr: frPublicSiteOverride,
  hi: hiPublicSiteOverride,
  pt: ptPublicSiteOverride,
  'zh-Hans': zhHansPublicSiteOverride,
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const mergeDeep = <T>(base: T, override: DeepPartial<T> | undefined): T => {
  if (override === undefined) return base
  if (Array.isArray(base)) {
    if (!Array.isArray(override)) return base
    return base.map((item, index) => mergeDeep(item, override[index] as DeepPartial<typeof item> | undefined)) as T
  }
  if (isPlainObject(base) && isPlainObject(override)) {
    const next: Record<string, unknown> = { ...base }
    for (const [key, value] of Object.entries(override)) {
      next[key] = mergeDeep((base as Record<string, unknown>)[key], value as DeepPartial<unknown>)
    }
    return next as T
  }
  return override as T
}

export const getPublicSiteContent = (localeInput?: string | null): PublicSiteNls => {
  const locale = normalizePublicSiteLocale(localeInput)
  const labels = PUBLIC_SITE_LOCALE_LABELS[locale]
  const override = OVERRIDES[locale]
  return {
    locale,
    label: labels.label,
    nativeLabel: labels.nativeLabel,
    content: mergeDeep(englishPublicSiteContent as PublicSiteContent, override?.content),
    chrome: mergeDeep(PUBLIC_SITE_CHROME as PublicSiteChrome, override?.chrome),
    typography: PUBLIC_SITE_TYPOGRAPHY[locale],
  }
}

export const getPublicSiteRouteMeta = (content: PublicSiteContent, path: string) =>
  content.routes.find((route) => route.path === path) ?? content.routes[0]
